import type { AppConfig } from './config';
import type { CallLog } from './calllog/calllog';
import type { CredentialStore } from './credentials';
import type { Ringtone } from './audio/ringtone';
import type { Phone } from './sip/phone';
import type { Store } from './state/store';
import { InvalidDurchwahlError, normalizeDialTarget, toSipExtension } from './extension';
import { AUTH_FAILED_MESSAGE, DISCONNECTED_MESSAGE, endCauseMessage } from './messages';

export interface AppDeps {
  config: AppConfig;
  phone: Phone;
  store: Store;
  callLog: CallLog;
  credentials: CredentialStore;
  ringtone: Ringtone;
  audioSink: (stream: MediaStream) => void;
  now?: () => number;
}

export interface App {
  start(): void;
  login(durchwahl: string, password: string, remember: boolean): string | null;
  logout(): void;
  dial(input: string): string | null;
  answer(): void;
  reject(): void;
  hangup(): void;
  toggleMute(): void;
  toggleHold(): void;
  dtmf(tone: string): void;
  viewCallLog(): void;
}

export function createApp(deps: AppDeps): App {
  const { phone, store, callLog, credentials, ringtone, config } = deps;
  const now = deps.now ?? Date.now;
  let pendingTarget: string | null = null;

  store.setState({ missedCount: callLog.missedSinceLastView() });

  const connectWith = (durchwahl: string, password: string) => {
    const sipExtension = toSipExtension(durchwahl, config.webrtcPrefix);
    store.setState({ session: { loggedIn: true, durchwahl: durchwahl.trim(), sipExtension }, message: null });
    phone.connect({ extension: sipExtension, password });
  };

  phone.on('registration', (r) => {
    store.setState({ registration: r.state });
    if (r.state === 'failed' && r.reason === 'auth') {
      credentials.clear();
      phone.disconnect();
      store.setState({ session: { loggedIn: false }, message: AUTH_FAILED_MESSAGE });
    }
  });

  phone.on('transport', ({ state }) => {
    store.setState({ transport: state, message: state === 'disconnected' ? DISCONNECTED_MESSAGE : null });
  });

  phone.on('incoming', ({ from, displayName }) => {
    ringtone.start('ring');
    store.setState({
      call: { status: 'ringing-in', direction: 'in', remote: from, displayName, startedAt: now(), muted: false, held: false },
    });
  });

  phone.on('callState', ({ state, direction, cause }) => {
    const call = store.getState().call;
    switch (state) {
      case 'calling':
        ringtone.start('ringback');
        store.setState({
          message: null,
          call: { status: 'calling', direction: 'out', remote: pendingTarget ?? '', startedAt: now(), muted: false, held: false },
        });
        break;
      case 'ringing-in':
        break; // bereits in 'incoming' gesetzt
      case 'established':
        ringtone.stop();
        if (call) store.setState({ call: { ...call, status: 'established', held: false, answeredAt: call.answeredAt ?? now() } });
        break;
      case 'held':
        if (call) store.setState({ call: { ...call, status: 'held', held: true } });
        break;
      case 'ended':
        ringtone.stop();
        pendingTarget = null;
        store.setState({ call: null, message: endCauseMessage(cause, direction) });
        break;
    }
  });

  phone.on('callEnded', (entry) => {
    callLog.add(entry);
    store.setState({ missedCount: callLog.missedSinceLastView() });
  });

  phone.on('remoteStream', ({ stream }) => deps.audioSink(stream));

  return {
    start() {
      const saved = credentials.load();
      if (!saved) return;
      try {
        connectWith(saved.durchwahl, saved.password);
      } catch {
        credentials.clear();
      }
    },
    login(durchwahl, password, remember) {
      if (!password) return 'Bitte das SIP-Passwort eingeben.';
      try {
        toSipExtension(durchwahl, config.webrtcPrefix);
      } catch (e) {
        if (e instanceof InvalidDurchwahlError) return 'Bitte eine gültige Durchwahl (nur Ziffern) eingeben.';
        throw e;
      }
      credentials.save({ durchwahl: durchwahl.trim(), password }, remember);
      connectWith(durchwahl, password);
      return null;
    },
    logout() {
      phone.disconnect();
      ringtone.stop();
      credentials.clear();
      store.setState({ session: { loggedIn: false }, registration: 'unregistered', call: null, message: null });
    },
    dial(input) {
      const target = normalizeDialTarget(input);
      if (!target) return 'Bitte eine gültige Rufnummer eingeben.';
      pendingTarget = target;
      try {
        phone.call(target);
      } catch {
        pendingTarget = null;
        return 'Nicht verbunden.';
      }
      return null;
    },
    answer() {
      ringtone.stop();
      phone.answer();
    },
    reject() {
      ringtone.stop();
      phone.reject();
    },
    hangup() {
      phone.hangup();
    },
    toggleMute() {
      const call = store.getState().call;
      if (!call || call.status === 'ringing-in') return;
      phone.setMuted(!call.muted);
      store.setState({ call: { ...call, muted: !call.muted } });
    },
    toggleHold() {
      const call = store.getState().call;
      if (!call || (call.status !== 'established' && call.status !== 'held')) return;
      phone.setHeld(!call.held);
    },
    dtmf(tone) {
      const call = store.getState().call;
      if (call?.status === 'established') phone.sendDtmf(tone);
    },
    viewCallLog() {
      callLog.markViewed();
      store.setState({ missedCount: 0 });
    },
  };
}
